import BackDrop from 'components/BackDrop'
import { createContext, useContext, useState, useEffect } from 'react'
import { getCompanyStatus, getUserInfo } from 'requests'
import sleep from 'utils/sleep'
import { useNavigate, useLocation } from 'react-router-dom'
import AuthenticationService from 'config/AuthenticationService'
import UserService from 'config/UserService'

export const NapCobranzas = createContext()

/**
 * @typedef {Object} NapCobranzasContextValue
 * @property {Object} userInfo - Información del usuario autenticado.
 * @property {Object} company - Información de la empresa seleccionada.
 * @property {Function} setCompany - Función para actualizar la información de la empresa.
 * @property {boolean} isAuth - Indica si el usuario está autenticado.
 * @property {Object} infoPayment - Información relacionada con el estado de pago de la empresa.
 * @property {boolean} isSuperAdmin - Indica si el usuario tiene rol de super administrador.
 */

/**
 * Custom hook para acceder al contexto de NapCobranzas.
 * Proporciona información y funciones relacionadas con el usuario, empresa y estado de autenticación.
 *
 * @returns {NapCobranzasContextValue} value - El valor del contexto de autenticación.
 */
export const useNapCobranzas = () => useContext(NapCobranzas)

const exclude = ['/home', '/login', '/forgot-password']

const NapCobranzasProvider = ({ children }) => {
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()
  const location = useLocation()
  // DATA CONTEXT
  const [userInfo, setUserInfo] = useState({})
  const [company, setCompany] = useState({})
  const [isAuth, setIsAuth] = useState(false)

  const [infoPayment, setInfoPayment] = useState({})

  useEffect(() => {
    if (!exclude.includes(location.pathname) || UserService.token()) getData()
    else setLoading(false)
  }, [])

  const getData = async () => {
    try {
      setLoading(true)
      const res = await getUserInfo()

      if (res.message) {
        setIsAuth(true)
        setUserInfo(res.data)
        const data = await getCompanyStatus(res.data.roles, company._id)
        setInfoPayment(data)
        if (location.pathname === '/login') navigate('/')
      } else {
        await redirectTO()
      }
    } catch (error) {
      await AuthenticationService.logout()
      await redirectTO()
    } finally {
      setLoading(false)
    }
  }

  const redirectTO = async () => {
    await sleep(200)
    navigate('/login')
  }

  const isSuperAdmin = userInfo?.roles?.includes?.('superadmin')

  return (
    <NapCobranzas.Provider value={{ userInfo, company, setCompany, isAuth, infoPayment, isSuperAdmin }}>
      {children}
      <BackDrop loading={loading} />
    </NapCobranzas.Provider>
  )
}
export default NapCobranzasProvider
